// /home/heliowallet/frontend/components/RpcTester.tsx
'use client';

import { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlusIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  ServerIcon,
  BoltIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  FireIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { rpcManager } from '@/services/rpcManager';
import Portal from './Portal';

interface RpcTesterProps {
  open: boolean;
  onClose: () => void;
  chainId: number;
  rpcs: string[];
  selectedRpc: string | null;
  onSelectRpc: (rpc: string, chainId?: number) => void;
}

interface RpcDisplay {
  url: string;
  host: string;
  fullUrl: string;
  protocol: string;
  path: string;
  health?: any;
}

interface TestProgress {
  total: number;
  tested: number;
  working: number;
  failed: number;
}

export default function RpcTester({ 
  open, 
  onClose, 
  chainId, 
  rpcs, 
  selectedRpc,
  onSelectRpc 
}: RpcTesterProps) {
  const [testResults, setTestResults] = useState<Map<string, any>>(new Map());
  const [testing, setTesting] = useState(false);
  const [customRpc, setCustomRpc] = useState('');
  const [addingCustom, setAddingCustom] = useState(false);
  const [testProgress, setTestProgress] = useState<TestProgress>({ total: 0, tested: 0, working: 0, failed: 0 });
  const [showResults, setShowResults] = useState<'all' | 'working' | 'failed'>('all');
  const [expandedSections, setExpandedSections] = useState({ working: true, failed: false, untested: false });
  
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel ongoing tests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Load cached results when opening
  useEffect(() => {
    if (open && rpcs.length > 0) {
      loadCachedResults();
    }
  }, [open, rpcs.length]);

  const loadCachedResults = () => {
    const results = new Map();
    rpcs.forEach(rpc => {
      const health = rpcManager.getRpcHealth(rpc);
      if (health) {
        results.set(rpc, health);
      }
    });
    setTestResults(results);
    updateProgress(results);
  };

  const updateProgress = (results: Map<string, any>) => {
    const working = Array.from(results.values()).filter(r => r.score > 0).length;
    const failed = Array.from(results.values()).filter(r => r.score === 0).length;
    setTestProgress({
      total: rpcs.length,
      tested: results.size,
      working,
      failed
    });
  };

  // Parse RPC URL for display
  const parseRpcUrl = (url: string): RpcDisplay => {
    try {
      const urlObj = new URL(url);
      return {
        url,
        host: urlObj.hostname,
        fullUrl: url,
        protocol: urlObj.protocol.replace(':', ''),
        path: urlObj.pathname === '/' ? '' : urlObj.pathname
      };
    } catch {
      return {
        url,
        host: url,
        fullUrl: url,
        protocol: 'unknown',
        path: ''
      };
    }
  };

  // Bulk test all RPCs - 100 at a time
  const bulkTestRpcs = async () => {
    if (testing || !isMountedRef.current) return;
    
    setTesting(true);
    setTestProgress({ total: rpcs.length, tested: 0, working: 0, failed: 0 });
    setShowResults('all');
    
    // Create abort controller
    abortControllerRef.current = new AbortController();
    
    try {
      toast.loading(`Testing ${rpcs.length} RPC endpoints...`, { id: 'rpc-bulk-test' });
      
      const results = new Map<string, any>();
      const batchSize = 100; // Test 100 RPCs at a time
      
      for (let i = 0; i < rpcs.length; i += batchSize) {
        if (!isMountedRef.current || abortControllerRef.current.signal.aborted) break;
        
        const batch = rpcs.slice(i, i + batchSize);
        
        // Test batch in parallel
        const batchPromises = batch.map(async (rpc) => {
          try {
            const result = await rpcManager.testRpc(rpc, chainId);
            return { rpc, result };
          } catch (error) {
            return {
              rpc,
              result: {
                url: rpc,
                latency: Infinity,
                blockNumber: 0,
                lastCheck: Date.now(),
                failCount: 1,
                score: 0
              }
            };
          }
        });
        
        // Wait for batch to complete
        const batchResults = await Promise.all(batchPromises);
        
        // Update results
        batchResults.forEach(({ rpc, result }) => {
          results.set(rpc, result);
        });
        
        // Update UI
        if (isMountedRef.current) {
          setTestResults(new Map(results));
          updateProgress(results);
        }
        
        // Small delay between batches
        if (i + batchSize < rpcs.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      if (isMountedRef.current && !abortControllerRef.current.signal.aborted) {
        const workingCount = Array.from(results.values()).filter(r => r.score > 0).length;
        toast.success(`Test complete: ${workingCount}/${rpcs.length} working`, { id: 'rpc-bulk-test' });
        
        // Auto-select best if current is not working
        const currentHealth = results.get(selectedRpc || '');
        if (!currentHealth || currentHealth.score === 0) {
          const bestRpc = await rpcManager.selectBestRpc(chainId, rpcs);
          if (bestRpc && isMountedRef.current) {
            handleSelectRpc(bestRpc);
          }
        }
      }
    } catch (error) {
      console.error('Error testing RPCs:', error);
      if (isMountedRef.current) {
        toast.error('Test failed', { id: 'rpc-bulk-test' });
      }
    } finally {
      if (isMountedRef.current) {
        setTesting(false);
        abortControllerRef.current = null;
      }
    }
  };

  const handleSelectRpc = (url: string) => {
    if (!isMountedRef.current) return;
    onSelectRpc(url, chainId);
    toast.success(`Selected: ${parseRpcUrl(url).host}`);
    onClose();
  };

  const handleAddCustomRpc = async () => {
    if (!customRpc.trim()) {
      toast.error('Please enter RPC URL');
      return;
    }

    // Validate URL
    try {
      const urlObj = new URL(customRpc);
      if (!['http:', 'https:', 'wss:'].includes(urlObj.protocol)) {
        toast.error('URL must use http://, https://, or wss:// protocol');
        return;
      }
    } catch {
      toast.error('Invalid URL format');
      return;
    }

    setAddingCustom(true);

    try {
      const result = await rpcManager.testRpc(customRpc, chainId);
      
      if (result.score > 0) {
        // Update results
        const newResults = new Map(testResults);
        newResults.set(customRpc, result);
        setTestResults(newResults);
        updateProgress(newResults);

        // Add to rpcs list if not exists
        if (!rpcs.includes(customRpc)) {
          rpcs.push(customRpc);
        }

        handleSelectRpc(customRpc);
        setCustomRpc('');
      } else {
        toast.error('RPC is not responding');
      }
    } finally {
      setAddingCustom(false);
    }
  };

  const cancelTest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      toast.error('Test cancelled', { id: 'rpc-bulk-test' });
    }
  };

  // Helper functions
  const getScoreIcon = (score: number) => {
    if (score >= 80) {
      return <SignalIcon className="h-5 w-5 text-green-500" />;
    } else if (score >= 50) {
      return <SignalIcon className="h-5 w-5 text-yellow-500" />;
    } else if (score > 0) {
      return <SignalIcon className="h-5 w-5 text-orange-500" />;
    } else {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 50) return 'Good';
    if (score > 0) return 'Fair';
    return 'Failed';
  };

  const formatLatency = (latency: number) => {
    if (latency === Infinity) return 'Failed';
    if (latency < 1000) return `${latency}ms`;
    return `${(latency / 1000).toFixed(1)}s`;
  };

  const toggleSection = (section: 'working' | 'failed' | 'untested') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!open) return null;

  // Sort and filter results
  const allResults = Array.from(testResults.entries()).map(([url, health]) => ({
    ...parseRpcUrl(url),
    health
  }));
  
  const workingResults = allResults
    .filter(r => r.health && r.health.score > 0)
    .sort((a, b) => {
      if (b.health.score !== a.health.score) return b.health.score - a.health.score;
      return a.health.latency - b.health.latency;
    });
    
  const failedResults = allResults.filter(r => r.health && r.health.score === 0);
  const untestedRpcs = rpcs.filter(url => !testResults.has(url)).map(url => parseRpcUrl(url));

  // Filter based on showResults
  const shouldShowWorking = showResults === 'all' || showResults === 'working';
  const shouldShowFailed = showResults === 'all' || showResults === 'failed';
  const shouldShowUntested = showResults === 'all';

  return (
    <Portal>
      <div className="fixed inset-0 z-[10000] overflow-y-auto">
        <div className="flex min-h-screen items-end sm:items-center justify-center p-0 sm:p-4">
          <div 
            className="fixed inset-0 bg-black/50 transition-opacity" 
            onClick={onClose}
          />
          
          {/* Mobile-optimized modal */}
          <div className="relative w-full sm:w-auto sm:max-w-3xl bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl transform transition-all modal-mobile sm:modal">
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <ServerIcon className="h-5 sm:h-6 w-5 sm:w-6" />
                    RPC Endpoints
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Chain ID: {chainId} • Total: {rpcs.length}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Progress Bar */}
              {testProgress.tested > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <span>{testProgress.tested}/{testProgress.total} tested</span>
                    <span>{testProgress.working} working</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full flex">
                      <div 
                        className="bg-green-500 transition-all duration-300"
                        style={{ width: `${(testProgress.working / testProgress.total) * 100}%` }}
                      />
                      <div 
                        className="bg-red-500 transition-all duration-300"
                        style={{ width: `${(testProgress.failed / testProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 sm:p-6 max-h-[70vh] sm:max-h-[600px] overflow-y-auto">
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                <button
                  onClick={bulkTestRpcs}
                  disabled={testing}
                  className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                  {testing ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      Testing {testProgress.tested}/{testProgress.total}
                    </>
                  ) : (
                    <>
                      <FireIcon className="h-4 w-4" />
                      Test All ({rpcs.length})
                    </>
                  )}
                </button>
                
                {testing ? (
                  <button
                    onClick={cancelTest}
                    className="px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    Cancel
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      const best = workingResults[0];
                      if (best) {
                        handleSelectRpc(best.url);
                      } else {
                        toast.error('No working RPCs found');
                      }
                    }}
                    disabled={workingResults.length === 0}
                    className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    <BoltIcon className="h-4 w-4" />
                    Use Best
                  </button>
                )}
              </div>

              {/* Add Custom RPC */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Add Custom RPC
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customRpc}
                    onChange={(e) => setCustomRpc(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomRpc()}
                    placeholder="https://your-custom-rpc.com"
                    className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    onClick={handleAddCustomRpc}
                    disabled={addingCustom || !customRpc.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                  >
                    {addingCustom ? (
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <PlusIcon className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">Add</span>
                  </button>
                </div>
              </div>

              {/* Results Filter - Mobile optimized */}
              <div className="flex gap-2 mb-4 overflow-x-auto">
                {['all', 'working', 'failed'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setShowResults(filter as any)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      showResults === filter
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {filter === 'all' && `All (${rpcs.length})`}
                    {filter === 'working' && `Working (${workingResults.length})`}
                    {filter === 'failed' && `Failed (${failedResults.length})`}
                  </button>
                ))}
              </div>

              {/* Best RPC Recommendation */}
              {workingResults.length > 0 && (
                <div className="mb-4 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <BoltIcon className="h-5 sm:h-6 w-5 sm:w-6 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-green-900 dark:text-green-100 text-sm">
                        Recommended RPC
                      </p>
                      <p className="text-xs sm:text-sm text-green-700 dark:text-green-300 break-all">
                        {workingResults[0].fullUrl}
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        {formatLatency(workingResults[0].health.latency)} • {getScoreLabel(workingResults[0].health.score)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleSelectRpc(workingResults[0].url)}
                      className="w-full sm:w-auto px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      Use This
                    </button>
                  </div>
                </div>
              )}

              {/* RPC Results */}
              <div className="space-y-3">
                {/* Working RPCs */}
                {shouldShowWorking && workingResults.length > 0 && (
                  <div>
                    <button
                      onClick={() => toggleSection('working')}
                      className="w-full flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <span className="text-sm font-medium text-green-600 dark:text-green-400">
                        Working RPCs ({workingResults.length})
                      </span>
                      <ChevronDownIcon className={`h-4 w-4 transform transition-transform ${expandedSections.working ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {expandedSections.working && (
                      <div className="mt-2 space-y-2">
                        {workingResults.slice(0, 20).map((rpc) => {
                          const isSelected = selectedRpc === rpc.url;
                          
                          return (
                            <div
                              key={rpc.url}
                              className={`p-3 rounded-lg border-2 transition-all ${
                                isSelected
                                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                                  : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2 flex-1 min-w-0">
                                  {getScoreIcon(rpc.health.score)}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                      {rpc.host}
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 break-all line-clamp-2">
                                      {rpc.fullUrl}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                      <span className="flex items-center gap-1">
                                        <ClockIcon className="h-3 w-3" />
                                        {formatLatency(rpc.health.latency)}
                                      </span>
                                      <span>•</span>
                                      <span>{getScoreLabel(rpc.health.score)}</span>
                                      {rpc.health.score >= 80 && (
                                        <ShieldCheckIcon className="h-3 w-3 text-green-500" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                <button
                                  onClick={() => handleSelectRpc(rpc.url)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${
                                    isSelected
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500'
                                  }`}
                                >
                                  {isSelected ? 'Selected' : 'Select'}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        {workingResults.length > 20 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                            +{workingResults.length - 20} more working
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Failed RPCs */}
                {shouldShowFailed && failedResults.length > 0 && (
                  <div>
                    <button
                      onClick={() => toggleSection('failed')}
                      className="w-full flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <span className="text-sm font-medium text-red-600 dark:text-red-400">
                        Failed RPCs ({failedResults.length})
                      </span>
                      <ChevronDownIcon className={`h-4 w-4 transform transition-transform ${expandedSections.failed ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {expandedSections.failed && (
                      <div className="mt-2 space-y-2">
                        {failedResults.slice(0, 10).map((rpc) => (
                          <div
                            key={rpc.url}
                            className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                          >
                            <div className="flex items-start gap-2">
                              <XCircleIcon className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-red-700 dark:text-red-300 truncate">
                                  {rpc.host}
                                </p>
                                <p className="text-xs text-red-600 dark:text-red-400 break-all line-clamp-2">
                                  {rpc.fullUrl}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {failedResults.length > 10 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                            +{failedResults.length - 10} more failed
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Untested RPCs */}
                {shouldShowUntested && untestedRpcs.length > 0 && (
                  <div>
                    <button
                      onClick={() => toggleSection('untested')}
                      className="w-full flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Untested ({untestedRpcs.length})
                      </span>
                      <ChevronDownIcon className={`h-4 w-4 transform transition-transform ${expandedSections.untested ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {expandedSections.untested && (
                      <div className="mt-2 space-y-2">
                        {untestedRpcs.slice(0, 10).map((rpc) => (
                          <div
                            key={rpc.url}
                            className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 opacity-60"
                          >
                            <div className="flex items-start gap-2">
                              <ClockIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                  {rpc.host}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 break-all line-clamp-2">
                                  {rpc.fullUrl}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {untestedRpcs.length > 10 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                            +{untestedRpcs.length - 10} more untested
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Empty state */}
                {rpcs.length === 0 && (
                  <div className="text-center py-8">
                    <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">
                      No RPC endpoints available
                    </p>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="mt-6 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex gap-3">
                  <GlobeAltIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <div className="text-xs sm:text-sm text-blue-700 dark:text-blue-300">
                    <p className="font-medium mb-1">Automatic Failover Enabled</p>
                    <p>If the selected RPC fails, HelioWallet will automatically switch to the next best available endpoint.</p>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              {testProgress.tested > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <ChartBarIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{testProgress.tested}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Tested</p>
                  </div>
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">{testProgress.working}</p>
                    <p className="text-xs text-green-600 dark:text-green-400">Working</p>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-red-600 dark:text-red-400">{testProgress.failed}</p>
                    <p className="text-xs text-red-600 dark:text-red-400">Failed</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

// Import ChevronDownIcon if not already imported
import { ChevronDownIcon } from '@heroicons/react/24/outline';